/**
 * DynamoDB Service Layer
 * Handles cross-account access to DynamoDB tables in account 305345571965
 */

import {
  DynamoDBClient,
  QueryCommand,
  ScanCommand,
  GetItemCommand,
  PutItemCommand,
  UpdateItemCommand,
  BatchGetItemCommand,
  QueryCommandInput,
  ScanCommandInput,
} from '@aws-sdk/client-dynamodb';
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb';
import { STSClient, AssumeRoleCommand } from '@aws-sdk/client-sts';
import { getMidwayCredentials } from '../auth/midway';
import { getEnvironmentConfig } from '../configurations/config';
import {
  ControlMapping,
  MappingReview,
  MappingFeedback,
  Framework,
  FrameworkControl,
  MappingQueryParams,
  ReviewQueryParams,
  DynamoDBQueryResponse,
  CrossAccountAccessError,
  MappingWithReviews,
  UserReview,
  FinalSubmission,
  CompositeKey,
  MappingStatus,
  ControlDomain,
  ControlObjective,
  CommonControl,
  CommonControlsHierarchyResponse,
  ControlHierarchy,
} from '../types/dynamodb';

// Table names
const TABLES = {
  CONTROL_MAPPINGS: 'ControlMappings',
  MAPPING_REVIEWS: 'MappingReviews',
  MAPPING_FEEDBACK: 'MappingFeedback',
  FRAMEWORKS: 'Frameworks',
  FRAMEWORK_CONTROLS: 'FrameworkControls',
} as const;

/**
 * DynamoDB Service Class
 * Provides methods for accessing DynamoDB tables with cross-account credentials
 */
export class DynamoDBService {
  private client: DynamoDBClient | null = null;
  private credentialsExpiry: Date | null = null;

  /**
   * Get or create DynamoDB client. When the environment provides a
   * crossAccountRoleArn we assume that role with Midway creds; otherwise we
   * use Midway creds directly (the Midway role already has DDB access in the
   * target account, e.g. Beta where tables live in the app account).
   */
  private async getClient(): Promise<DynamoDBClient> {
    const envConfig = getEnvironmentConfig();
    const { dynamoDbAccountId, dynamoDbRegion, dynamoDbCrossAccountRoleArn } = envConfig;

    // Check if credentials are still valid (refresh 5 minutes before expiry)
    if (
      this.client &&
      this.credentialsExpiry &&
      this.credentialsExpiry.getTime() - Date.now() > 5 * 60 * 1000
    ) {
      return this.client;
    }

    try {
      const midwayCredentials = await getMidwayCredentials();

      if (!dynamoDbCrossAccountRoleArn) {
        // Direct Midway access — no AssumeRole needed. Track the Midway
        // credential expiry so we rebuild the client when Midway refreshes.
        this.client = new DynamoDBClient({
          region: dynamoDbRegion,
          credentials: midwayCredentials,
        });
        this.credentialsExpiry =
          midwayCredentials.expiration ?? new Date(Date.now() + 15 * 60 * 1000);
        return this.client;
      }

      const stsClient = new STSClient({
        region: dynamoDbRegion,
        credentials: midwayCredentials,
      });

      const assumeRoleCommand = new AssumeRoleCommand({
        RoleArn: dynamoDbCrossAccountRoleArn,
        RoleSessionName: `nexus-ui-${Date.now()}`,
        DurationSeconds: 3600,
        ExternalId: 'nexus-ui-dynamodb-access-2025',
      });

      const assumeRoleResponse = await stsClient.send(assumeRoleCommand);

      if (!assumeRoleResponse.Credentials) {
        throw new Error('Failed to assume cross-account role: No credentials returned');
      }

      this.client = new DynamoDBClient({
        region: dynamoDbRegion,
        credentials: {
          accessKeyId: assumeRoleResponse.Credentials.AccessKeyId!,
          secretAccessKey: assumeRoleResponse.Credentials.SecretAccessKey!,
          sessionToken: assumeRoleResponse.Credentials.SessionToken!,
        },
      });

      this.credentialsExpiry = assumeRoleResponse.Credentials.Expiration!;

      return this.client;
    } catch (error) {
      console.error('Failed to initialize DynamoDB client:', error);
      throw new CrossAccountAccessError(
        'Failed to access DynamoDB',
        dynamoDbAccountId,
        dynamoDbRegion,
        error as Error
      );
    }
  }

  // ==========================================================================
  // Control Mappings Operations
  // ==========================================================================

  /**
   * Query control mappings by various parameters
   */
  async queryControlMappings(
    params: MappingQueryParams
  ): Promise<DynamoDBQueryResponse<ControlMapping>> {
    const client = await this.getClient();

    try {
      let command;

      if (params.status) {
        // Query using StatusIndex
        const input: QueryCommandInput = {
          TableName: TABLES.CONTROL_MAPPINGS,
          IndexName: 'StatusIndex',
          KeyConditionExpression: '#status = :status',
          ExpressionAttributeNames: {
            '#status': 'status',
          },
          ExpressionAttributeValues: marshall({
            ':status': params.status,
          }),
          Limit: params.limit || 100,
          ExclusiveStartKey: params.nextToken
            ? JSON.parse(atob(params.nextToken))
            : undefined,
        };
        command = new QueryCommand(input);
      } else if (params.workflowKey) {
        // Query using WorkflowIndex
        const input: QueryCommandInput = {
          TableName: TABLES.CONTROL_MAPPINGS,
          IndexName: 'WorkflowIndex',
          KeyConditionExpression: 'mappingWorkflowKey = :workflowKey',
          ExpressionAttributeValues: marshall({
            ':workflowKey': params.workflowKey,
          }),
          Limit: params.limit || 100,
          ExclusiveStartKey: params.nextToken
            ? JSON.parse(atob(params.nextToken))
            : undefined,
        };
        command = new QueryCommand(input);
      } else if (params.controlKey) {
        // Query by partition key
        const input: QueryCommandInput = {
          TableName: TABLES.CONTROL_MAPPINGS,
          KeyConditionExpression: 'controlKey = :controlKey',
          ExpressionAttributeValues: marshall({
            ':controlKey': params.controlKey,
          }),
          Limit: params.limit || 100,
          ExclusiveStartKey: params.nextToken
            ? JSON.parse(atob(params.nextToken))
            : undefined,
        };
        command = new QueryCommand(input);
      } else {
        // Scan all mappings
        const input: ScanCommandInput = {
          TableName: TABLES.CONTROL_MAPPINGS,
          Limit: params.limit || 100,
          ExclusiveStartKey: params.nextToken
            ? JSON.parse(atob(params.nextToken))
            : undefined,
        };
        command = new ScanCommand(input);
      }

      const response = await client.send(command);

      return {
        items: (response.Items || []).map((item) => unmarshall(item) as ControlMapping),
        count: response.Count || 0,
        scannedCount: response.ScannedCount || 0,
        lastEvaluatedKey: response.LastEvaluatedKey,
        nextToken: response.LastEvaluatedKey
          ? btoa(JSON.stringify(response.LastEvaluatedKey))
          : undefined,
      };
    } catch (error) {
      console.error('Error querying control mappings:', error);
      throw error;
    }
  }

  /**
   * Get a single control mapping by mappingKey
   */
  async getControlMappingByKey(mappingKey: string): Promise<ControlMapping | null> {
    const client = await this.getClient();

    try {
      const command = new QueryCommand({
        TableName: TABLES.CONTROL_MAPPINGS,
        IndexName: 'MappingKeyIndex',
        KeyConditionExpression: 'mappingKey = :mappingKey',
        ExpressionAttributeValues: marshall({
          ':mappingKey': mappingKey,
        }),
        Limit: 1,
      });

      const response = await client.send(command);

      if (!response.Items || response.Items.length === 0) {
        return null;
      }

      return unmarshall(response.Items[0]) as ControlMapping;
    } catch (error) {
      console.error('Error getting control mapping:', error);
      throw error;
    }
  }

  // ==========================================================================
  // Mapping Reviews Operations
  // ==========================================================================

  /**
   * Query reviews for a specific mapping
   */
  async getReviewsForMapping(mappingKey: string): Promise<MappingReview[]> {
    const client = await this.getClient();

    try {
      const command = new QueryCommand({
        TableName: TABLES.MAPPING_REVIEWS,
        KeyConditionExpression: 'mappingKey = :mappingKey',
        ExpressionAttributeValues: marshall({
          ':mappingKey': mappingKey,
        }),
        ScanIndexForward: false, // Sort by reviewKey descending (newest first)
      });

      const response = await client.send(command);

      return (response.Items || []).map((item) => unmarshall(item) as MappingReview);
    } catch (error) {
      console.error('Error getting reviews for mapping:', error);
      throw error;
    }
  }

  /**
   * Query reviews by reviewer
   */
  async getReviewsByReviewer(
    params: ReviewQueryParams
  ): Promise<DynamoDBQueryResponse<MappingReview>> {
    const client = await this.getClient();

    try {
      let keyConditionExpression = 'reviewerId = :reviewerId';
      const expressionAttributeValues: Record<string, any> = {
        ':reviewerId': params.reviewerId,
      };

      if (params.startDate && params.endDate) {
        keyConditionExpression += ' AND submittedAt BETWEEN :startDate AND :endDate';
        expressionAttributeValues[':startDate'] = params.startDate;
        expressionAttributeValues[':endDate'] = params.endDate;
      } else if (params.startDate) {
        keyConditionExpression += ' AND submittedAt >= :startDate';
        expressionAttributeValues[':startDate'] = params.startDate;
      }

      const command = new QueryCommand({
        TableName: TABLES.MAPPING_REVIEWS,
        IndexName: 'ReviewerIndex',
        KeyConditionExpression: keyConditionExpression,
        ExpressionAttributeValues: marshall(expressionAttributeValues),
        Limit: params.limit || 100,
        ScanIndexForward: false,
          ExclusiveStartKey: params.nextToken
            ? JSON.parse(atob(params.nextToken))
            : undefined,
      });

      const response = await client.send(command);

      return {
        items: (response.Items || []).map((item) => unmarshall(item) as MappingReview),
        count: response.Count || 0,
        scannedCount: response.ScannedCount || 0,
        lastEvaluatedKey: response.LastEvaluatedKey,
        nextToken: response.LastEvaluatedKey
          ? btoa(JSON.stringify(response.LastEvaluatedKey))
          : undefined,
      };
    } catch (error) {
      console.error('Error getting reviews by reviewer:', error);
      throw error;
    }
  }

  /**
   * Submit a new review
   */
  async submitReview(review: Omit<MappingReview, 'reviewKey' | 'submittedAt'>): Promise<void> {
    const client = await this.getClient();

    try {
      const reviewKey = `review_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const submittedAt = new Date().toISOString();

      const command = new PutItemCommand({
        TableName: TABLES.MAPPING_REVIEWS,
        Item: marshall({
          ...review,
          reviewKey,
          submittedAt,
        }),
      });

      await client.send(command);
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  }

  /**
   * Suggest a new mapping
   * Creates a new mapping-review record with status 'suggested'
   */
  async suggestMapping(params: {
    controlKey: string;
    mappedControlKey: string;
    reviewerId: string;
    reviewerName: string;
    justification: string;
  }): Promise<void> {
    const client = await this.getClient();

    try {
      const mappingKey = `${params.controlKey}#${params.mappedControlKey}`;
      const reviewKey = `suggestion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const submittedAt = new Date().toISOString();

      const command = new PutItemCommand({
        TableName: TABLES.MAPPING_REVIEWS,
        Item: marshall({
          mappingKey,
          reviewKey,
          controlKey: params.controlKey,
          mappedControlKey: params.mappedControlKey,
          reviewerId: params.reviewerId,
          reviewerName: params.reviewerName,
          submittedAt,
          status: 'suggested',
          decision: 'suggest',
          justification: params.justification,
          isSuggestion: true, // Mark this as the original suggestion record
          submittedBy: {
            type: 'Human',
            userId: params.reviewerId,
            userName: params.reviewerName,
            timestamp: submittedAt,
          },
        }),
      });

      await client.send(command);
      console.log(`Successfully submitted mapping suggestion: ${mappingKey}`);
    } catch (error) {
      console.error('Error submitting mapping suggestion:', error);
      throw error;
    }
  }

  /**
   * Submit a community label for a mapping
   * Writes to MappingFeedback table (one label per user per mapping)
   */
  async submitLabel(params: {
    mappingKey: string;
    controlKey: string;
    mappedControlKey: string;
    reviewerId: string;
    reviewerName: string;
    decision: 'label-yes' | 'label-no';
    feedback?: string;
  }): Promise<void> {
    const client = await this.getClient();

    try {
      const timestamp = new Date().toISOString();

      const command = new PutItemCommand({
        TableName: TABLES.MAPPING_FEEDBACK,
        Item: marshall({
          mappingKey: params.mappingKey,
          reviewerId: params.reviewerId,
          reviewerName: params.reviewerName,
          timestamp,
          justification: `${params.decision}: ${params.feedback || 'No additional feedback'}`,
          isEdgeCaseMapping: false,
          status: 'submitted',
          // Store additional context as extra attributes (DynamoDB allows this)
          labelDecision: params.decision,
          controlKey: params.controlKey,
          mappedControlKey: params.mappedControlKey,
        }),
      });

      await client.send(command);
      console.log(`Successfully submitted label for ${params.mappingKey}: ${params.decision}`);
    } catch (error) {
      console.error('Error submitting label:', error);
      throw error;
    }
  }

  /**
   * Get all mappings for community labeling
   * Returns mappings with current user's feedback only (blind labeling)
   */
  async getAllMappingsForLabeling(currentUserId: string): Promise<MappingWithReviews[]> {
    const client = await this.getClient();

    try {
      // Scan the mapping-reviews table to get ALL source records with pagination
      const allReviewRecords: MappingReview[] = [];
      let lastEvaluatedKey: Record<string, any> | undefined = undefined;
      
      do {
        const reviewsCommand: ScanCommand = new ScanCommand({
          TableName: TABLES.MAPPING_REVIEWS,
          ExclusiveStartKey: lastEvaluatedKey,
          Limit: 1000,
        });

        const reviewsResponse = await client.send(reviewsCommand);
        const batch = (reviewsResponse.Items || []).map((item: any) => unmarshall(item) as MappingReview);
        allReviewRecords.push(...batch);
        
        lastEvaluatedKey = reviewsResponse.LastEvaluatedKey;
        
        console.log(`✅ Loaded ${allReviewRecords.length} records so far from mapping-reviews table...`);
      } while (lastEvaluatedKey);

      const reviewRecords = allReviewRecords;
      console.log(`✅ Total records loaded for labeling: ${reviewRecords.length}`);

      // Group records by mappingKey to find source records
      const recordsByMapping = new Map<string, MappingReview[]>();
      reviewRecords.forEach((record) => {
        const existing = recordsByMapping.get(record.mappingKey) || [];
        existing.push(record);
        recordsByMapping.set(record.mappingKey, existing);
      });

      // Get all feedback for current user from MappingFeedback table
      const feedbackCommand = new ScanCommand({
        TableName: TABLES.MAPPING_FEEDBACK,
        FilterExpression: 'reviewerId = :userId',
        ExpressionAttributeValues: marshall({
          ':userId': currentUserId,
        }),
      });

      const feedbackResponse = await client.send(feedbackCommand);
      const userFeedback = (feedbackResponse.Items || []).map((item) => unmarshall(item) as MappingFeedback);
      
      // Create a map of user's feedback by mappingKey
      const feedbackByMapping = new Map<string, MappingFeedback>();
      userFeedback.forEach((feedback) => {
        feedbackByMapping.set(feedback.mappingKey, feedback);
      });

      console.log(`Loaded ${userFeedback.length} feedback records for current user`);

      // Create MappingWithReviews objects
      const mappingsForLabeling: MappingWithReviews[] = [];
      
      for (const [mappingKey, allRecords] of recordsByMapping.entries()) {
        // Find source record (machine submission or suggestion)
        const machineSubmission = allRecords.find(r => r.submittedBy?.type === 'Machine');
        const suggestionRecord = allRecords.find(r => (r as any).isSuggestion === true);
        const recordWithKeys = allRecords.find(r => r.controlKey && r.mappedControlKey);
        
        const sourceRecord = machineSubmission || suggestionRecord || recordWithKeys || allRecords[0];
        
        // Skip if missing required fields
        if (!sourceRecord.controlKey || !sourceRecord.mappedControlKey) {
          console.warn(`⚠️ Skipping mapping ${mappingKey} - missing controlKey or mappedControlKey`);
          continue;
        }
        
        // Get current user's feedback if exists
        const userFeedbackRecord = feedbackByMapping.get(mappingKey);
        
        // Construct mapping object
        const mapping: ControlMapping = {
          controlKey: sourceRecord.controlKey,
          mappedControlKey: sourceRecord.mappedControlKey,
          mappingKey: sourceRecord.mappingKey,
          status: sourceRecord.status as MappingStatus,
          mappingWorkflowKey: '',
          timestamp: sourceRecord.submittedAt,
        };

        // Add to results (feedback contains user's label if they submitted one)
        mappingsForLabeling.push({
          mapping: mapping,
          reviews: [], // Empty for blind labeling (don't show other users' labels)
          feedback: userFeedbackRecord,
        });
      }

      console.log(`Returning ${mappingsForLabeling.length} mappings for labeling`);
      return mappingsForLabeling;
    } catch (error) {
      console.error('Error getting mappings for labeling:', error);
      throw error;
    }
  }

  // ==========================================================================
  // Mapping Feedback Operations
  // ==========================================================================

  /**
   * Submit final feedback/submission (for ReviewMappings locked feedback)
   * NOTE: This is different from submitFeedback() which is for voting (thumbs up/down)
   */
  async submitFinalFeedback(
    feedback: Omit<MappingFeedback, 'timestamp'>
  ): Promise<void> {
    const client = await this.getClient();

    try {
      const timestamp = new Date().toISOString();

      const command = new PutItemCommand({
        TableName: TABLES.MAPPING_FEEDBACK,
        Item: marshall({
          ...feedback,
          timestamp,
        }),
      });

      await client.send(command);
    } catch (error: any) {
      // If table doesn't exist yet, silently skip feedback submission
      if (error.name === 'ResourceNotFoundException') {
        console.warn('Feedback table not available yet, skipping feedback submission');
        return;
      }
      console.error('Error submitting feedback:', error);
      throw error;
    }
  }

  /**
   * Get feedback for a specific mapping
   */
  async getFeedbackForMapping(mappingKey: string): Promise<MappingFeedback[]> {
    const client = await this.getClient();

    try {
      const command = new QueryCommand({
        TableName: TABLES.MAPPING_FEEDBACK,
        KeyConditionExpression: 'mappingKey = :mappingKey',
        ExpressionAttributeValues: marshall({
          ':mappingKey': mappingKey,
        }),
      });

      const response = await client.send(command);

      return (response.Items || []).map((item) => unmarshall(item) as MappingFeedback);
    } catch (error: any) {
      // If table doesn't exist yet, return empty array silently
      if (error.name === 'ResourceNotFoundException') {
        return [];
      }
      console.error('Error getting feedback for mapping:', error);
      throw error;
    }
  }

  // ==========================================================================
  // Framework Controls Operations
  // ==========================================================================

  /**
   * Get control details from framework-controls table
   */
  async getFrameworkControl(
    frameworkKey: string,
    controlKey: string
  ): Promise<FrameworkControl | null> {
    const client = await this.getClient();

    try {
      const command = new GetItemCommand({
        TableName: TABLES.FRAMEWORK_CONTROLS,
        Key: marshall({
          frameworkKey,
          controlKey,
        }),
      });

      const response = await client.send(command);

      if (!response.Item) {
        return null;
      }

      return unmarshall(response.Item) as FrameworkControl;
    } catch (error: any) {
      if (error.name === 'ResourceNotFoundException') {
        console.warn(`Table ${TABLES.FRAMEWORK_CONTROLS} not found`);
        return null;
      }
      console.error('Error getting framework control:', error);
      return null;
    }
  }

  /**
   * Get all available frameworks
   */
  async getAvailableFrameworks(): Promise<Framework[]> {
    const client = await this.getClient();

    try {
      const command = new ScanCommand({
        TableName: TABLES.FRAMEWORKS,
      });

      const response = await client.send(command);

      return (response.Items || []).map((item) => unmarshall(item) as Framework);
    } catch (error: any) {
      if (error.name === 'ResourceNotFoundException') {
        console.warn(`Table ${TABLES.FRAMEWORKS} not found`);
        return [];
      }
      console.error('Error getting available frameworks:', error);
      throw error;
    }
  }

  /**
   * Get all controls for a specific framework
   */
  async getFrameworkControls(catalog: string, version: string): Promise<FrameworkControl[]> {
    const client = await this.getClient();

    try {
      const frameworkKey = `${catalog}#${version}`;
      
      const command = new QueryCommand({
        TableName: TABLES.FRAMEWORK_CONTROLS,
        KeyConditionExpression: 'frameworkKey = :frameworkKey',
        ExpressionAttributeValues: marshall({
          ':frameworkKey': frameworkKey,
        }),
      });

      const response = await client.send(command);

      return (response.Items || []).map((item) => unmarshall(item) as FrameworkControl);
    } catch (error: any) {
      if (error.name === 'ResourceNotFoundException') {
        console.warn(`Table ${TABLES.FRAMEWORK_CONTROLS} not found`);
        return [];
      }
      console.error('Error getting framework controls:', error);
      throw error;
    }
  }

  /**
   * Get all controls for a specific framework by frameworkKey
   */
  async getFrameworkControlsByKey(frameworkKey: string): Promise<FrameworkControl[]> {
    const client = await this.getClient();

    try {
      const command = new QueryCommand({
        TableName: TABLES.FRAMEWORK_CONTROLS,
        KeyConditionExpression: 'frameworkKey = :frameworkKey',
        ExpressionAttributeValues: marshall({
          ':frameworkKey': frameworkKey,
        }),
      });

      const response = await client.send(command);

      return (response.Items || []).map((item) => unmarshall(item) as FrameworkControl);
    } catch (error: any) {
      if (error.name === 'ResourceNotFoundException') {
        console.warn(`Table ${TABLES.FRAMEWORK_CONTROLS} not found`);
        return [];
      }
      console.error('Error getting framework controls by key:', error);
      throw error;
    }
  }

  /**
   * Batch get multiple framework controls
   */
  async batchGetFrameworkControls(
    controls: Array<{ frameworkKey: string; controlKey: string }>
  ): Promise<Map<string, FrameworkControl>> {
    const client = await this.getClient();
    const results = new Map<string, FrameworkControl>();

    if (controls.length === 0) {
      console.log('⚠️ batchGetFrameworkControls: No controls to fetch');
      return results;
    }

    try {
      // Filter out invalid controls and deduplicate
      const uniqueControls = new Map<string, { frameworkKey: string; controlKey: string }>();
      controls.forEach((control) => {
        // Skip if frameworkKey or controlKey is empty/undefined
        if (!control.frameworkKey || !control.controlKey) {
          console.warn('⚠️ Skipping invalid control:', control);
          return;
        }
        const key = `${control.frameworkKey}#${control.controlKey}`;
        uniqueControls.set(key, control);
      });

      const deduplicatedControls = Array.from(uniqueControls.values());
      
      if (deduplicatedControls.length === 0) {
        console.log('⚠️ No valid controls to fetch after filtering');
        return results;
      }

      // FrameworkControls rows carry large additionalInfo blobs; a 100-key batch
      // can exceed the 16MB BatchGetItem response limit and drop items silently.
      // Small batches + retry on UnprocessedKeys keeps the call complete.
      const batchSize = 25;
      const concurrency = 6;

      const runBatch = async (batch: typeof deduplicatedControls): Promise<void> => {
        let keys = batch.map((c) =>
          marshall(
            { frameworkKey: c.frameworkKey, controlKey: c.controlKey },
            { removeUndefinedValues: true }
          )
        );
        let attempts = 0;
        while (keys.length > 0 && attempts < 8) {
          const resp = await client.send(
            new BatchGetItemCommand({
              RequestItems: { [TABLES.FRAMEWORK_CONTROLS]: { Keys: keys } },
            })
          );
          const items = resp.Responses?.[TABLES.FRAMEWORK_CONTROLS] || [];
          for (const item of items) {
            const control = unmarshall(item) as FrameworkControl;
            const key = `${control.frameworkKey}#${control.controlKey}`;
            results.set(key, control);
          }
          const unprocessed =
            resp.UnprocessedKeys?.[TABLES.FRAMEWORK_CONTROLS]?.Keys || [];
          keys = unprocessed;
          if (keys.length > 0) {
            await new Promise((r) => setTimeout(r, 100 * Math.pow(2, attempts)));
          }
          attempts++;
        }
      };

      const batches: Array<typeof deduplicatedControls> = [];
      for (let i = 0; i < deduplicatedControls.length; i += batchSize) {
        batches.push(deduplicatedControls.slice(i, i + batchSize));
      }
      for (let i = 0; i < batches.length; i += concurrency) {
        await Promise.all(batches.slice(i, i + concurrency).map(runBatch));
      }

      console.log(
        `✅ Total controls loaded: ${results.size} / ${deduplicatedControls.length} requested`
      );
      return results;
    } catch (error: any) {
      if (error.name === 'ResourceNotFoundException') {
        console.error(`❌ Table ${TABLES.FRAMEWORK_CONTROLS} not found`);
        return results;
      }
      console.error('❌ Error batch getting framework controls:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        code: error.code,
        statusCode: error.$metadata?.httpStatusCode
      });
      return results;
    }
  }

  // ==========================================================================
  // Aggregated Data Operations
  // ==========================================================================

  /**
   * Get complete mapping data with reviews and feedback
   */
  async getMappingWithReviews(mappingKey: string): Promise<MappingWithReviews | null> {
    try {
      const [mapping, reviews, feedbackList] = await Promise.all([
        this.getControlMappingByKey(mappingKey),
        this.getReviewsForMapping(mappingKey),
        this.getFeedbackForMapping(mappingKey),
      ]);

      if (!mapping) {
        return null;
      }

      // Get the locked feedback if it exists
      const feedback = feedbackList.find((f) => f.status === 'locked');

      return {
        mapping,
        reviews,
        feedback,
      };
    } catch (error) {
      console.error('Error getting mapping with reviews:', error);
      throw error;
    }
  }

  /**
   * Get all mappings with their reviews and feedback
   * This is the main method for populating the ReviewMappings component
   * Queries the mapping-reviews table directly with pagination to load ALL records
   */
  async getAllMappingsWithReviews(
    params?: MappingQueryParams
  ): Promise<MappingWithReviews[]> {
    const client = await this.getClient();

    try {
      // Scan the mapping-reviews table to get ALL review records with pagination
      const allReviewRecords: MappingReview[] = [];
      let lastEvaluatedKey: Record<string, any> | undefined = undefined;
      
      do {
        const command: ScanCommand = new ScanCommand({
          TableName: TABLES.MAPPING_REVIEWS,
          ExclusiveStartKey: lastEvaluatedKey,
          Limit: 1000, // Larger batches for efficiency
        });

        const response = await client.send(command);
        const batch = (response.Items || []).map((item: any) => unmarshall(item) as MappingReview);
        allReviewRecords.push(...batch);
        
        lastEvaluatedKey = response.LastEvaluatedKey;
        
        console.log(`✅ Loaded ${allReviewRecords.length} records so far from mapping-reviews table...`);
      } while (lastEvaluatedKey);

      const reviewRecords = allReviewRecords;
      console.log(`✅ Total records loaded: ${reviewRecords.length}`);

      // Group records by mappingKey
      const recordsByMapping = new Map<string, MappingReview[]>();
      reviewRecords.forEach((record) => {
        const existing = recordsByMapping.get(record.mappingKey) || [];
        existing.push(record);
        recordsByMapping.set(record.mappingKey, existing);
      });

      // Create MappingWithReviews objects without fetching feedback (optimization)
      const mappingsWithReviews: MappingWithReviews[] = [];
      
      for (const [mappingKey, allRecords] of recordsByMapping.entries()) {
        // Find the source record with mapping data:
        // 1. Machine submissions (ML-generated mappings)
        // 2. Suggestion records (marked with isSuggestion flag)
        // 3. Any record that has controlKey and mappedControlKey
        const machineSubmission = allRecords.find(r => r.submittedBy?.type === 'Machine');
        const suggestionRecord = allRecords.find(r => (r as any).isSuggestion === true);
        const recordWithKeys = allRecords.find(r => r.controlKey && r.mappedControlKey);
        
        const sourceRecord = machineSubmission || suggestionRecord || recordWithKeys || allRecords[0];
        
        // Skip if we don't have the required fields
        if (!sourceRecord.controlKey || !sourceRecord.mappedControlKey) {
          console.warn(`⚠️ Skipping mapping ${mappingKey} - missing controlKey or mappedControlKey`);
          continue;
        }
        
        // Filter human reviews (exclude machine submissions, original suggestion, AND labels)
        const humanReviews = allRecords.filter(r => 
          r.submittedBy?.type !== 'Machine' && 
          !(r as any).isSuggestion &&
          r.decision !== 'label-yes' &&
          r.decision !== 'label-no'
        );
        
        // Construct a ControlMapping object from the source record
        const mapping: ControlMapping = {
          controlKey: sourceRecord.controlKey,
          mappedControlKey: sourceRecord.mappedControlKey,
          mappingKey: sourceRecord.mappingKey,
          status: sourceRecord.status as MappingStatus,
          mappingWorkflowKey: '', // Not available in mapping-reviews table
          timestamp: sourceRecord.submittedAt,
        };

        // Determine if this is a user-suggested mapping
        const isSuggested = sourceRecord === suggestionRecord && suggestionRecord !== undefined;

        // Skip feedback fetching for now to improve load performance
        mappingsWithReviews.push({
          mapping: {
            ...mapping,
            isSuggested, // Add flag to indicate user-suggested mappings
          } as any,
          reviews: humanReviews.sort((a, b) => 
            new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
          ),
          feedback: undefined,
        });
      }

      // Also scan ControlMappings table and include mappings not already in MappingReviews
      const existingMappingKeys = new Set(mappingsWithReviews.map(m => m.mapping.mappingKey));
      let cmLastKey: Record<string, any> | undefined = undefined;

      do {
        const cmCommand = new ScanCommand({
          TableName: TABLES.CONTROL_MAPPINGS,
          ExclusiveStartKey: cmLastKey,
          Limit: 1000,
        });
        const cmResponse = await client.send(cmCommand);
        const cmBatch = (cmResponse.Items || []).map((item: any) => unmarshall(item) as ControlMapping);

        for (const cm of cmBatch) {
          if (!existingMappingKeys.has(cm.mappingKey)) {
            existingMappingKeys.add(cm.mappingKey);
            mappingsWithReviews.push({
              mapping: cm,
              reviews: [],
              feedback: undefined,
            });
          }
        }

        cmLastKey = cmResponse.LastEvaluatedKey;
      } while (cmLastKey);

      // Dedupe bidirectional pairs (A→B and B→A share the same mappingKey) and
      // orient so controlKey is the AWS side — the UI groups parent rows by it.
      const isAwsKey = (k?: string) => !!k && k.startsWith('AWS.ControlCatalog#');
      const orientAwsFirst = (m: MappingWithReviews): MappingWithReviews => {
        const mk = m.mapping;
        if (isAwsKey(mk.controlKey) || !isAwsKey(mk.mappedControlKey)) return m;
        return {
          ...m,
          mapping: {
            ...mk,
            controlKey: mk.mappedControlKey,
            mappedControlKey: mk.controlKey,
          },
        };
      };

      const byKey = new Map<string, MappingWithReviews>();
      for (const entry of mappingsWithReviews) {
        const key = entry.mapping.mappingKey;
        if (!key) continue;
        const oriented = orientAwsFirst(entry);
        const existing = byKey.get(key);
        if (!existing) {
          byKey.set(key, oriented);
        } else {
          const mergedReviews = [...existing.reviews, ...oriented.reviews].sort(
            (a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
          );
          existing.reviews = mergedReviews;
        }
      }
      const deduped = Array.from(byKey.values());

      console.log(`Returning ${deduped.length} unique mappings (${mappingsWithReviews.length} raw rows)`);
      return deduped;
    } catch (error) {
      console.error('Error getting all mappings with reviews:', error);
      throw error;
    }
  }

  // ==========================================================================
  // Common Controls Hierarchy (NEX-1062 - Compass Ontology)
  // ==========================================================================

  /**
   * Get Common Controls with extracted hierarchy for cascading filters
   * Fetches all controls from Common-Controls framework and extracts unique
   * domains (L1), objectives (L2), and controls (L3) with their relationships
   *
   * @returns Structured hierarchy data for UI cascading dropdowns
   */
  async getCommonControlsWithHierarchy(): Promise<CommonControlsHierarchyResponse> {
    const FRAMEWORK_NAME = 'Common-Controls';
    const FRAMEWORK_VERSION = '1.0';
    const frameworkKey = `${FRAMEWORK_NAME}#${FRAMEWORK_VERSION}`;

    try {
      // Fetch all controls from Common-Controls framework
      const allControls = await this.getFrameworkControlsByKey(frameworkKey);

      // Maps for deduplication
      const domainsMap = new Map<string, ControlDomain>();
      const objectivesMap = new Map<string, ControlObjective>();
      const controls: CommonControl[] = [];

      // Process each control and extract hierarchy
      for (const control of allControls) {
        const hierarchy = this.extractHierarchy(control);
        if (!hierarchy) {
          console.warn(`Control ${control.controlKey} has no hierarchy data`);
          continue;
        }

        // Add domain (L1) if not already present
        if (hierarchy.l1 && !domainsMap.has(hierarchy.l1.code)) {
          domainsMap.set(hierarchy.l1.code, {
            code: hierarchy.l1.code,
            title: hierarchy.l1.title,
            description: hierarchy.l1.description || '',
          });
        }

        // Add objective (L2) if not already present
        if (hierarchy.l2 && !objectivesMap.has(hierarchy.l2.code)) {
          objectivesMap.set(hierarchy.l2.code, {
            code: hierarchy.l2.code,
            title: hierarchy.l2.title,
            description: hierarchy.l2.description || '',
            domainCode: hierarchy.l1?.code || '',
          });
        }

        // Add control (L3)
        controls.push({
          controlKey: control.controlKey,
          code: hierarchy.l3?.code || control.controlId || '',
          title: hierarchy.l3?.title || control.title || '',
          description: hierarchy.l3?.description || control.description || '',
          objectiveCode: hierarchy.l2?.code || '',
          domainCode: hierarchy.l1?.code || '',
          hierarchy,
        });
      }

      // Sort results alphabetically by title for consistent UI display
      const domains = Array.from(domainsMap.values()).sort((a, b) =>
        a.title.localeCompare(b.title)
      );
      const objectives = Array.from(objectivesMap.values()).sort((a, b) =>
        a.title.localeCompare(b.title)
      );
      controls.sort((a, b) => a.title.localeCompare(b.title));

      console.log(`✅ Loaded Common Controls hierarchy: ${domains.length} domains, ${objectives.length} objectives, ${controls.length} controls`);

      return { domains, objectives, controls };
    } catch (error) {
      console.error('Error getting Common Controls hierarchy:', error);
      throw error;
    }
  }

  /**
   * Extract hierarchy data from a FrameworkControl's additionalInfo field
   * The hierarchy is stored in additionalInfo as a DynamoDB List structure
   */
  private extractHierarchy(control: FrameworkControl): ControlHierarchy | null {
    try {
      const additionalInfo = control.additionalInfo;
      if (!additionalInfo) return null;

      // Handle different possible structures from DynamoDB
      let hierarchyData: any = null;

      if (Array.isArray(additionalInfo)) {
        // DynamoDB List format: additionalInfo[0].data.hierarchy
        const firstItem = additionalInfo[0];
        if (firstItem?.data?.hierarchy) {
          hierarchyData = firstItem.data.hierarchy;
        } else if (firstItem?.M?.data?.M?.hierarchy) {
          // Raw DynamoDB format with type markers
          hierarchyData = this.parseDynamoDBMap(firstItem.M.data.M.hierarchy);
        }
      } else if (additionalInfo.hierarchy) {
        // Direct hierarchy object
        hierarchyData = additionalInfo.hierarchy;
      } else if (additionalInfo.L) {
        // Raw DynamoDB List format
        const firstItem = additionalInfo.L[0];
        if (firstItem?.M?.data?.M?.hierarchy) {
          hierarchyData = this.parseDynamoDBMap(firstItem.M.data.M.hierarchy);
        }
      }

      if (!hierarchyData) return null;

      // Extract L1, L2, L3 levels
      return {
        l1: this.extractHierarchyLevel(hierarchyData.l1 || hierarchyData.L1),
        l2: this.extractHierarchyLevel(hierarchyData.l2 || hierarchyData.L2),
        l3: this.extractHierarchyLevel(hierarchyData.l3 || hierarchyData.L3),
      };
    } catch (error) {
      console.warn(`Failed to extract hierarchy for control ${control.controlKey}:`, error);
      return null;
    }
  }

  /**
   * Extract a single hierarchy level from potentially DynamoDB-formatted data
   */
  private extractHierarchyLevel(levelData: any): { code: string; title: string; description: string } {
    if (!levelData) {
      return { code: '', title: '', description: '' };
    }

    // Handle raw DynamoDB map format with type markers (M, S, etc.)
    if (levelData.M) {
      return {
        code: levelData.M.code?.S || '',
        title: levelData.M.title?.S || '',
        description: levelData.M.description?.S || '',
      };
    }

    // Handle already-parsed object format
    return {
      code: levelData.code || '',
      title: levelData.title || '',
      description: levelData.description || '',
    };
  }

  /**
   * Parse a DynamoDB Map structure into a plain JavaScript object
   */
  private parseDynamoDBMap(dynamoMap: any): any {
    if (!dynamoMap) return null;

    if (dynamoMap.M) {
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(dynamoMap.M)) {
        result[key] = this.parseDynamoDBMap(value);
      }
      return result;
    }

    if (dynamoMap.S) {
      return dynamoMap.S;
    }

    if (dynamoMap.N) {
      return Number(dynamoMap.N);
    }

    if (dynamoMap.L) {
      return dynamoMap.L.map((item: any) => this.parseDynamoDBMap(item));
    }

    // Already a plain value or object
    if (typeof dynamoMap === 'object' && dynamoMap !== null) {
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(dynamoMap)) {
        result[key] = this.parseDynamoDBMap(value);
      }
      return result;
    }

    return dynamoMap;
  }

  // ==========================================================================
  // Feedback API Methods (for ViewMappings voting - NEX-1064)
  // ==========================================================================

  /**
   * Get current user's feedback for multiple mappings
   * Uses BatchGetItem for O(k) efficiency instead of full table scan
   * Matches the nexusApiService signature for compatibility
   */
  async getUserFeedbackForMappings(
    mappingIds: string[],
    userId: string
  ): Promise<Map<string, { label: string; feedback?: string; feedbackId: string }>> {
    const client = await this.getClient();
    const userFeedbackMap = new Map<string, { label: string; feedback?: string; feedbackId: string }>();

    if (mappingIds.length === 0 || !userId) {
      return userFeedbackMap;
    }

    try {
      // Build composite keys for BatchGetItem (mappingKey=PK, reviewerId=SK)
      // This is O(k) where k = number of mappingIds, vs O(n) table scan
      const keys = mappingIds.map((mappingId) =>
        marshall({
          mappingKey: mappingId,
          reviewerId: userId,
        })
      );

      // DynamoDB BatchGetItem supports up to 100 items per request
      const batchSize = 100;
      for (let i = 0; i < keys.length; i += batchSize) {
        const batch = keys.slice(i, i + batchSize);

        const command = new BatchGetItemCommand({
          RequestItems: {
            [TABLES.MAPPING_FEEDBACK]: {
              Keys: batch,
            },
          },
        });

        const response = await client.send(command);
        const items = response.Responses?.[TABLES.MAPPING_FEEDBACK] || [];

        items.forEach((item) => {
          const feedback = unmarshall(item) as MappingFeedback & { labelDecision?: string };
          userFeedbackMap.set(feedback.mappingKey, {
            label: feedback.labelDecision || (feedback.justification?.startsWith('label-') ? feedback.justification.split(':')[0] : 'thumbs_up'),
            feedback: feedback.justification,
            feedbackId: `${feedback.mappingKey}#${feedback.reviewerId}`,
          });
        });
      }

      return userFeedbackMap;
    } catch (error) {
      console.error('Error getting user feedback for mappings:', error);
      return userFeedbackMap;
    }
  }

  /**
   * Submit feedback (thumbs up/down) for a mapping
   * Matches the nexusApiService signature for compatibility
   */
  async submitFeedback(
    mappingId: string,
    data: {
      feedbackProviderId: string;
      label: 'thumbs_up' | 'thumbs_down';
      decision: 'agree' | 'disagree';
      feedback?: string;
    }
  ): Promise<{ message: string; mappingKey: string; feedbackId: string; label: string }> {
    const client = await this.getClient();

    try {
      const timestamp = new Date().toISOString();
      const feedbackId = `${mappingId}#${data.feedbackProviderId}`;

      const command = new PutItemCommand({
        TableName: TABLES.MAPPING_FEEDBACK,
        Item: marshall({
          mappingKey: mappingId,
          reviewerId: data.feedbackProviderId,
          reviewerName: data.feedbackProviderId, // Use ID as name if not provided
          timestamp,
          justification: data.feedback || `${data.label}: ${data.decision}`,
          isEdgeCaseMapping: false,
          status: 'submitted',
          labelDecision: data.label,
        }),
      });

      await client.send(command);

      return {
        message: 'Feedback submitted successfully',
        mappingKey: mappingId,
        feedbackId,
        label: data.label,
      };
    } catch (error) {
      console.error('Error submitting mapping feedback:', error);
      throw error;
    }
  }

  /**
   * Update existing feedback for a mapping
   * Matches the nexusApiService signature for compatibility
   */
  async updateFeedback(
    mappingId: string,
    feedbackId: string,
    data: {
      label?: 'thumbs_up' | 'thumbs_down';
      decision?: 'agree' | 'disagree';
      feedback?: string;
    }
  ): Promise<{ message: string; mappingKey: string; feedbackId: string }> {
    const client = await this.getClient();

    try {
      // Extract reviewerId from feedbackId (format: mappingKey#reviewerId)
      const reviewerId = feedbackId.split('#').pop() || feedbackId;

      // Build update expression dynamically
      const updateExpressions: string[] = [];
      const expressionAttributeValues: Record<string, any> = {};
      const expressionAttributeNames: Record<string, string> = {};

      if (data.label) {
        updateExpressions.push('#labelDecision = :label');
        expressionAttributeNames['#labelDecision'] = 'labelDecision';
        expressionAttributeValues[':label'] = data.label;
      }

      if (data.feedback !== undefined) {
        updateExpressions.push('justification = :justification');
        expressionAttributeValues[':justification'] = data.feedback || `${data.label}: ${data.decision}`;
      }

      updateExpressions.push('#ts = :timestamp');
      expressionAttributeNames['#ts'] = 'timestamp';
      expressionAttributeValues[':timestamp'] = new Date().toISOString();

      const command = new UpdateItemCommand({
        TableName: TABLES.MAPPING_FEEDBACK,
        Key: marshall({
          mappingKey: mappingId,
          reviewerId: reviewerId,
        }),
        UpdateExpression: `SET ${updateExpressions.join(', ')}`,
        ExpressionAttributeNames: expressionAttributeNames,
        ExpressionAttributeValues: marshall(expressionAttributeValues),
      });

      await client.send(command);

      return {
        message: 'Feedback updated successfully',
        mappingKey: mappingId,
        feedbackId,
      };
    } catch (error) {
      console.error('Error updating mapping feedback:', error);
      throw error;
    }
  }
}

// Singleton instance
export const dynamoDBService = new DynamoDBService();
